import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Grid,
  TextField,
  Slider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip
} from '@mui/material';
import {
  ArrowBack,
  Restaurant,
  LocalHospital,
  Favorite,
  Add,
  Save,
  Cancel,
  PersonPin,
  MedicalInformation,
  Print,
  Assignment,
  MedicalServices
} from '@mui/icons-material';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { vitalsAPI, ipdAPI, prognosisAPI } from '../../services/api';
import DietPlanManagement from './DietPlanManagement';
import TreatmentPlanManagement from './TreatmentPlanManagement';
import IPDAdmissionForms from '../IPDAdmissionForms';

function VitalsTab({ admissionId, patientId, filterFrom, filterTo }) {
  const [vitals, setVitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    bloodPressure: null,
    temperature: null,
    pulseRate: null,
    respiratoryRate: null,
    weight: null,
    height: null,
    oxygenSaturation: null,
    rbs: null,
    insulinUnits: null,
    painScale: 0,
    notes: ''
  });

  useEffect(() => {
    loadVitals();
  }, [admissionId]);

  const loadVitals = async () => {
    setLoading(true);
    try {
      const response = await vitalsAPI.getPatientVitals(admissionId);
      const data = response.data.data || response.data;
      setVitals(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load vitals history');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!patientId) {
      setError('Patient ID not available. Cannot record vitals.');
      return;
    }
    setSaving(true);
    try {
      await vitalsAPI.recordVitals(admissionId, patientId, form);
      setSuccess('Vitals recorded successfully');
      setAddOpen(false);
      setForm({
        bloodPressure: '', temperature: '', pulseRate: '',
        respiratoryRate: '', weight: '', height: '',
        oxygenSaturation: '', rbs: '', insulinUnits: '',
        painScale: 0, notes: ''
      });
      loadVitals();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to record vitals');
    } finally {
      setSaving(false);
    }
  };

  const formatDateTime = (str) => new Date(str).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });

  const displayVitals = (filterFrom || filterTo)
    ? vitals.filter(v => {
        const d = (v.recordedAt || v.createdAt || '').slice(0, 10);
        return (!filterFrom || d >= filterFrom) && (!filterTo || d <= filterTo);
      })
    : vitals;

  if (loading) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
      </Snackbar>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" color="error" startIcon={<Add />} onClick={() => setAddOpen(true)}>
          Record Vitals
        </Button>
      </Box>

      {displayVitals.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
          <Favorite sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography color="text.secondary">{(filterFrom || filterTo) ? 'No vitals recorded for this period' : 'No vitals recorded yet'}</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Date/Time</strong></TableCell>
                <TableCell><strong>BP</strong></TableCell>
                <TableCell><strong>Temp (°F)</strong></TableCell>
                <TableCell><strong>Pulse</strong></TableCell>
                <TableCell><strong>RR</strong></TableCell>
                <TableCell><strong>SpO2 (%)</strong></TableCell>
                <TableCell><strong>Weight (kg)</strong></TableCell>
                <TableCell><strong>RBS</strong></TableCell>
                <TableCell><strong>Insulin (U)</strong></TableCell>
                <TableCell><strong>Pain</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayVitals.map((v, idx) => (
                <TableRow key={idx} hover>
                  <TableCell>{formatDateTime(v.recordedAt || v.createdAt)}</TableCell>
                  <TableCell>{v.bloodPressure || '-'}</TableCell>
                  <TableCell>{v.temperature || '-'}</TableCell>
                  <TableCell>{v.pulseRate || '-'}</TableCell>
                  <TableCell>{v.respiratoryRate || '-'}</TableCell>
                  <TableCell>{v.oxygenSaturation || '-'}</TableCell>
                  <TableCell>{v.weight || '-'}</TableCell>
                  <TableCell>{v.rbs || '-'}</TableCell>
                  <TableCell>{v.insulinUnits || '-'}</TableCell>
                  <TableCell>
                    {v.painScale !== null && v.painScale !== undefined
                      ? <Chip label={v.painScale} size="small" color={v.painScale >= 7 ? 'error' : v.painScale >= 4 ? 'warning' : 'success'} />
                      : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Vitals Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Record Vitals</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth label="Blood Pressure (e.g. 120/80)" value={form.bloodPressure}
                onChange={e => setForm(f => ({ ...f, bloodPressure: e.target.value }))} size="small" />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth label="Temperature (°F)" type="number" value={form.temperature}
                onChange={e => setForm(f => ({ ...f, temperature: e.target.value }))} size="small" />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth label="Pulse Rate (bpm)" type="number" value={form.pulseRate}
                onChange={e => setForm(f => ({ ...f, pulseRate: e.target.value }))} size="small" />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth label="Respiratory Rate (/min)" type="number" value={form.respiratoryRate}
                onChange={e => setForm(f => ({ ...f, respiratoryRate: e.target.value }))} size="small" />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth label="Weight (kg)" type="number" value={form.weight}
                onChange={e => setForm(f => ({ ...f, weight: e.target.value }))} size="small" />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth label="Height (cm)" type="number" value={form.height}
                onChange={e => setForm(f => ({ ...f, height: e.target.value }))} size="small" />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth label="SpO2 (%)" type="number" value={form.oxygenSaturation}
                onChange={e => setForm(f => ({ ...f, oxygenSaturation: e.target.value }))} size="small" />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth label="RBS (mg/dL)" type="number" value={form.rbs}
                onChange={e => setForm(f => ({ ...f, rbs: e.target.value }))} size="small" />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth label="Insulin Units" type="number" value={form.insulinUnits}
                onChange={e => setForm(f => ({ ...f, insulinUnits: e.target.value }))} size="small" />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" gutterBottom>Pain Scale: {form.painScale}</Typography>
              <Slider
                value={form.painScale}
                onChange={(_, val) => setForm(f => ({ ...f, painScale: val }))}
                min={0} max={10} step={1}
                marks valueLabelDisplay="auto"
                color={form.painScale >= 7 ? 'error' : form.painScale >= 4 ? 'warning' : 'success'}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Notes" value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} size="small" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} startIcon={<Cancel />} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="error"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
            disabled={saving}>
            {saving ? 'Saving...' : 'Save Vitals'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function PrognosisTab({ admissionId, patientId, filterFrom, filterTo }) {
  const [prognosis, setPrognosis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    progressiveNote: '',
    doctorsOrder: '',
    recordedAt: ''
  });

  useEffect(() => {
    loadPrognosis();
  }, [admissionId]);

  const loadPrognosis = async () => {
    setLoading(true);
    try {
      const response = await prognosisAPI.getIPD(admissionId);
      const data = response.data.data || response.data;
      setPrognosis(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load prognosis history');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!patientId) {
      setError('Patient ID not available. Cannot record prognosis.');
      return;
    }
    if (!form.progressiveNote && !form.doctorsOrder) {
      setError('Please provide at least a Progressive Note or a Doctor\'s Order.');
      return;
    }
    setSaving(true);
    try {
      await prognosisAPI.recordIPD(admissionId, patientId, {
        ...form,
        recordedAt: form.recordedAt || undefined
      });
      setSuccess('Prognosis recorded successfully');
      setAddOpen(false);
      setForm({
        progressiveNote: '',
        doctorsOrder: '',
        recordedAt: ''
      });
      loadPrognosis();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to record prognosis');
    } finally {
      setSaving(false);
    }
  };

  const formatDateTime = (str) => new Date(str).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });

  const displayPrognosis = (filterFrom || filterTo)
    ? prognosis.filter(p => {
        const d = (p.recorded_at || p.created_at || '').slice(0, 10);
        return (!filterFrom || d >= filterFrom) && (!filterTo || d <= filterTo);
      })
    : prognosis;

  if (loading) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
      </Snackbar>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" color="error" startIcon={<Add />} onClick={() => setAddOpen(true)}>
          Record Prognosis
        </Button>
      </Box>

      {displayPrognosis.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
          <Assignment sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography color="text.secondary">{(filterFrom || filterTo) ? 'No prognosis records for this period' : 'No prognosis records yet'}</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell style={{ width: '150px' }}><strong>Date/Time</strong></TableCell>
                <TableCell><strong>Progressive Note</strong></TableCell>
                <TableCell><strong>Doctor's Order</strong></TableCell>
                <TableCell style={{ width: '150px' }}><strong>Recorded By</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayPrognosis.map((p, idx) => (
                <TableRow key={idx} hover>
                  <TableCell>{formatDateTime(p.recorded_at || p.created_at)}</TableCell>
                  <TableCell style={{ whiteSpace: 'pre-line' }}>{p.progressive_note || '-'}</TableCell>
                  <TableCell style={{ whiteSpace: 'pre-line' }}>{p.doctors_order || '-'}</TableCell>
                  <TableCell>
                    {p.recorded_by_name || '-'}
                    {p.recorded_by_role && <Typography variant="caption" display="block" color="text.secondary">{p.recorded_by_role}</Typography>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Prognosis Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Daily Prognosis</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Date & Time (Optional)"
                type="datetime-local"
                value={form.recordedAt}
                onChange={e => setForm(f => ({ ...f, recordedAt: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Progressive Note"
                value={form.progressiveNote}
                onChange={e => setForm(f => ({ ...f, progressiveNote: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Doctor's Order"
                value={form.doctorsOrder}
                onChange={e => setForm(f => ({ ...f, doctorsOrder: e.target.value }))}
                size="small"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} startIcon={<Cancel />} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="error"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
            disabled={saving}>
            {saving ? 'Saving...' : 'Save Prognosis'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Patient Info / Clinical Notes Tab ────────────────────────────────────────
function PatientInfoTab({ admissionId, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    chiefComplaint: '',
    diagnosis: '',
    pastMedicalHistory: '',
    currentMedications: '',
    familyHistory: '',
    doctorNotes: ''
  });

  useEffect(() => { load(); }, [admissionId]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await ipdAPI.getAdmissionDetails(admissionId);
      const d = res.data.data;
      setData(d);
      setForm({
        chiefComplaint:      d.chief_complaint      || '',
        diagnosis:           d.diagnosis            || '',
        pastMedicalHistory:  d.past_medical_history || '',
        currentMedications:  d.current_medications  || '',
        familyHistory:       d.family_history       || '',
        doctorNotes:         d.doctor_notes         || ''
      });
    } catch {
      setError('Failed to load admission details');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await ipdAPI.updateClinicalNotes(admissionId, form);
      setSuccess('Clinical notes saved successfully');
      if (onSaved) onSaved(form);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  const f = (key) => ({ value: form[key], onChange: e => setForm(p => ({ ...p, [key]: e.target.value })) });

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
      </Snackbar>

      {/* Patient demographics (read-only) */}
      {data && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
            Patient Information
          </Typography>
          <Grid container spacing={1.5}>
            {[
              ['Name',          `${data.first_name} ${data.last_name}`],
              ['UHID',          data.patient_code],
              ['Age / Gender',  `${data.age ?? '—'} yrs / ${data.gender || '—'}`],
              ['Phone',         data.phone || '—'],
              ['Email',         data.email || '—'],
              ['Address',       data.address || '—'],
              ['Room',          `${data.room_number} (${data.category_name})`],
              ['Admitted',      new Date(data.admission_date).toLocaleDateString('en-IN')],
              ['Days',          `${data.days_admitted} / ${data.planned_days || '—'}`],
            ].map(([label, val]) => (
              <Grid item xs={12} sm={6} md={4} key={label}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="body2" fontWeight="medium">{val}</Typography>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Editable clinical notes */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" color="primary">
            Clinical Notes
          </Typography>
          <Button variant="contained" size="small" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Notes'}
          </Button>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline rows={3} label="Chief Complaint"
              placeholder="Primary reason for admission…" {...f('chiefComplaint')} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline rows={3} label="Diagnosis"
              placeholder="Clinical diagnosis…" {...f('diagnosis')} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline rows={3} label="Past Medical History"
              placeholder="Previous illnesses, surgeries, hospitalisations…" {...f('pastMedicalHistory')} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline rows={3} label="Current Medications"
              placeholder="Ongoing medications and dosage…" {...f('currentMedications')} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline rows={2} label="Family History"
              placeholder="Relevant family medical history…" {...f('familyHistory')} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline rows={2} label="Doctor's Notes"
              placeholder="Observations, instructions, remarks…" {...f('doctorNotes')} />
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}

function IPDPatientManager() {
  const { admissionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const stateInfo = location.state || {};

  const [activeTab, setActiveTab] = useState(0);
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [admission, setAdmission] = useState(null);   // full admission + clinical notes
  const [showForms, setShowForms] = useState(false);

  const reloadAdmission = () => {
    if (!admissionId) return;
    ipdAPI.getAdmissionDetails(admissionId)
      .then(res => setAdmission(res.data.data))
      .catch(() => {}); // fallback to stateInfo if fetch fails
  };

  useEffect(() => {
    reloadAdmission();
  }, [admissionId]);

  // Merge: fetched data takes precedence; fall back to navigation state
  const patientName   = admission ? `${admission.first_name} ${admission.last_name}` : stateInfo.patientName || 'IPD Patient';
  const patientCode   = admission?.patient_code  || stateInfo.patientCode  || '-';
  const roomNumber    = admission?.room_number   || stateInfo.roomNumber   || '-';
  const admissionDate = admission?.admission_date|| stateInfo.admissionDate|| null;
  const plannedDays   = admission?.planned_days  || stateInfo.plannedDays  || 0;
  const patientId     = admission?.patient_id    || stateInfo.patientId    || null;

  const minDate = admissionDate ? new Date(admissionDate).toISOString().split('T')[0] : undefined;
  const maxDate = admissionDate && plannedDays
    ? (() => { const d = new Date(admissionDate); d.setDate(d.getDate() + plannedDays - 1); return d.toISOString().split('T')[0]; })()
    : undefined;

  const hasFilter = filterFrom || filterTo;

  // Build patient/admission objects for IPDAdmissionForms
  const formsPatient = admission ? {
    firstName:   admission.first_name,
    lastName:    admission.last_name,
    patientCode: admission.patient_code,
    phone:       admission.phone,
    gender:      admission.gender,
    dateOfBirth: admission.date_of_birth,
    email:       admission.email,
    address:     admission.address,
    fatherHusbandName: admission.father_husband_name,
    father_husband_name: admission.father_husband_name,
  } : null;

  const formsAdmission = admission ? {
    admissionId:         admission.id || admissionId,
    id:                  admission.id || admissionId,
    roomNumber:          admission.room_number,
    admissionDate:       admission.admission_date,
    diagnosis:           admission.diagnosis           || '',
    chiefComplaint:      admission.chief_complaint     || '',
    pastMedicalHistory:  admission.past_medical_history|| '',
    currentMedications:  admission.current_medications || '',
    familyHistory:       admission.family_history      || '',
    doctorNotes:         admission.doctor_notes        || '',
  } : null;

  // Called by PatientInfoTab after a successful save — keep admission in sync
  const handleNotesSaved = (form) => {
    setAdmission(prev => prev ? {
      ...prev,
      chief_complaint:      form.chiefComplaint,
      diagnosis:            form.diagnosis,
      past_medical_history: form.pastMedicalHistory,
      current_medications:  form.currentMedications,
      family_history:       form.familyHistory,
      doctor_notes:         form.doctorNotes,
    } : prev);
  };

  return (
    <Box>
      {/* Page header */}
      <Card elevation={2} sx={{ mb: 2 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton onClick={() => navigate('/doctor', { state: { activeTab: 3 } })} sx={{ mr: 1 }}>
                <ArrowBack />
              </IconButton>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {patientName} — Room {roomNumber}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  UHID: {patientCode}
                  {admissionDate && ` • Admitted: ${new Date(admissionDate).toLocaleDateString('en-GB')}`}
                  {plannedDays > 0 && ` • ${plannedDays} days`}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="outlined" size="small" startIcon={<Print />}
                onClick={() => setShowForms(true)}
                disabled={!formsPatient}
              >
                Print Forms
              </Button>
              <Button
                variant="outlined" size="small" color="secondary" startIcon={<MedicalServices />}
                onClick={() => navigate(`/doctor/discharge-prescription/${admissionId}`)}
              >
                Discharge Prescription
              </Button>
              <TextField
                label="From" type="date" size="small" value={filterFrom}
                onChange={e => setFilterFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: minDate, max: filterTo || maxDate }}
                sx={{ width: 160 }}
              />
              <TextField
                label="To" type="date" size="small" value={filterTo}
                onChange={e => setFilterTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: filterFrom || minDate, max: maxDate }}
                sx={{ width: 160 }}
              />
              {hasFilter && (
                <Button size="small" onClick={() => { setFilterFrom(''); setFilterTo(''); }}>Clear</Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<MedicalInformation />} iconPosition="start" label="Patient Info" />
          <Tab icon={<Restaurant />} iconPosition="start" label="Diet Plan" />
          <Tab icon={<LocalHospital />} iconPosition="start" label="Treatment Plan" />
          <Tab icon={<Favorite />} iconPosition="start" label="Vitals" />
          <Tab icon={<Assignment />} iconPosition="start" label="Prognosis" />
        </Tabs>
      </Box>

      <Box sx={{ pt: 2 }}>
        {activeTab === 0 && <PatientInfoTab admissionId={admissionId} onSaved={handleNotesSaved} />}
        {activeTab === 1 && <DietPlanManagement embedded filterFrom={filterFrom} filterTo={filterTo} />}
        {activeTab === 2 && <TreatmentPlanManagement embedded filterFrom={filterFrom} filterTo={filterTo} />}
        {activeTab === 3 && (
          <VitalsTab
            admissionId={admissionId}
            patientId={patientId}
            filterFrom={filterFrom}
            filterTo={filterTo}
          />
        )}
        {activeTab === 4 && (
          <PrognosisTab
            admissionId={admissionId}
            patientId={patientId}
            filterFrom={filterFrom}
            filterTo={filterTo}
          />
        )}
      </Box>

      {showForms && formsPatient && (
        <IPDAdmissionForms
          patient={formsPatient}
          admission={formsAdmission}
          onClose={() => {
            setShowForms(false);
            reloadAdmission();
          }}
        />
      )}
    </Box>
  );
}

export default IPDPatientManager;
